/**
 * Pool Liquidity Cache Service
 *
 * Provides granular caching for pool liquidity data with:
 * - Independent cache entries for each pool
 * - Status tracking (idle, loading, loaded, failed)
 * - Priority retry for failed entries
 */

import { Interface, formatUnits } from 'ethers'
import { getChainProvider } from '@/lib/chainProvider'
import { getMulticall3Address } from '@/lib/chains'
import {
  CHAIN_PRICE_CONFIGS,
  TOKEN_PRICE_CONFIGS,
  LPPoolConfig,
} from '@/lib/tokenPriceConfig'
import { getTokenPriceUsd, getNativeTokenPriceUsd } from './tokenPriceService'
import { createMulticallService } from './multicallService'

// ERC20 ABI for balance checks
const ERC20_ABI = ['function balanceOf(address owner) view returns (uint256)']

// Pool ABI to get token addresses
const POOL_ABI = [
  'function token0() view returns (address)',
  'function token1() view returns (address)',
]

// Cache entry status
export type CacheStatus = 'idle' | 'loading' | 'loaded' | 'failed'

// Pool info with explorer/dexscreener links
export interface PoolLinks {
  explorer: string
  dexscreener: string
}

export interface PoolLiquidityData {
  chainId: number
  chainName: string
  poolAddress: string
  dex: string

  // Token reserves
  token0Address: string
  token0Symbol: string
  token0Balance: bigint
  token0BalanceFormatted: string
  token0Decimals: number

  token1Address: string
  token1Symbol: string
  token1Balance: bigint
  token1BalanceFormatted: string
  token1Decimals: number

  // Prices
  token0PriceUsd: number | null
  token1PriceUsd: number | null

  // TVL
  token0ValueUsd: number | null
  token1ValueUsd: number | null
  totalValueUsd: number | null

  // Links
  links: PoolLinks
}

// Cache entry structure
export interface PoolCacheEntry {
  status: CacheStatus
  data: PoolLiquidityData | null
  error: string | null
  lastAttempt: number
  retryCount: number
}

// Pool identifier for cache keys
export interface PoolId {
  chainId: number
  poolAddress: string
  tokenSymbol: string // WELEPHANT or ELEPHANT
  dex: string
}

// Get all pool IDs from config
export function getAllPoolIds(): PoolId[] {
  const poolIds: PoolId[] = []

  for (const [tokenSymbol, tokenConfig] of Object.entries(TOKEN_PRICE_CONFIGS)) {
    for (const chainConfig of tokenConfig.chains) {
      for (const pool of chainConfig.pools) {
        poolIds.push({
          chainId: chainConfig.chainId,
          poolAddress: pool.address,
          tokenSymbol,
          dex: pool.dex,
        })
      }
    }
  }

  return poolIds
}

// Create cache key from pool ID
export function getPoolCacheKey(poolId: PoolId): string {
  return `${poolId.chainId}-${poolId.poolAddress}`
}

// Initial cache entry
export function createInitialPoolEntry(): PoolCacheEntry {
  return {
    status: 'idle',
    data: null,
    error: null,
    lastAttempt: 0,
    retryCount: 0,
  }
}

// Create initial pool cache
export function createInitialPoolCache(): Map<string, PoolCacheEntry> {
  const cache = new Map<string, PoolCacheEntry>()
  for (const poolId of getAllPoolIds()) {
    cache.set(getPoolCacheKey(poolId), createInitialPoolEntry())
  }
  return cache
}

// Check if error is transient (worth retrying)
function isTransientError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase()
  return (
    msg.includes('timeout') ||
    msg.includes('network') ||
    msg.includes('econnrefused') ||
    msg.includes('econnreset') ||
    msg.includes('rate limit') ||
    msg.includes('429') ||
    msg.includes('503') ||
    msg.includes('missing revert data')
  )
}

// Retry delays (exponential backoff)
const RETRY_DELAYS = [2000, 4000, 8000, 16000]
const MAX_RETRIES = 4

// Get retry delay based on retry count
export function getRetryDelay(retryCount: number): number {
  return RETRY_DELAYS[Math.min(retryCount, RETRY_DELAYS.length - 1)]
}

// Check if entry should be retried
export function shouldRetryPool(entry: PoolCacheEntry): boolean {
  if (entry.status !== 'failed') return false
  if (entry.retryCount >= MAX_RETRIES) return false
  const delay = getRetryDelay(entry.retryCount)
  return Date.now() - entry.lastAttempt >= delay
}

// Helper functions
function getPoolExplorerUrl(chainId: number, poolAddress: string): string {
  if (chainId === 8453) {
    return `https://basescan.org/address/${poolAddress}`
  } else if (chainId === 56) {
    return `https://bscscan.com/address/${poolAddress}`
  }
  return ''
}

function getDexscreenerUrl(chainId: number, poolAddress: string): string {
  const chainSlug = chainId === 8453 ? 'base' : chainId === 56 ? 'bsc' : ''
  if (!chainSlug) return ''
  return `https://dexscreener.com/${chainSlug}/${poolAddress}`
}

function getTokenSymbol(chainId: number, tokenAddress: string): string {
  const chainConfig = CHAIN_PRICE_CONFIGS[chainId]
  if (!chainConfig) return 'UNKNOWN'

  const addr = tokenAddress.toLowerCase()

  // Check if it's the native wrapped token
  if (addr === chainConfig.nativeToken.wrappedAddress.toLowerCase()) {
    return `W${chainConfig.nativeToken.symbol}`
  }

  // Check if it's a stablecoin
  for (const stablecoin of chainConfig.stablecoins) {
    if (addr === stablecoin.address.toLowerCase()) {
      return stablecoin.symbol
    }
  }

  // Check all configured tokens
  for (const [symbol, tokenConfig] of Object.entries(TOKEN_PRICE_CONFIGS)) {
    for (const chain of tokenConfig.chains) {
      if (chain.chainId === chainId && addr === chain.address.toLowerCase()) {
        return symbol
      }
    }
  }

  return 'UNKNOWN'
}

function getTokenDecimals(chainId: number, tokenAddress: string): number {
  const chainConfig = CHAIN_PRICE_CONFIGS[chainId]
  if (!chainConfig) return 18

  // Check if it's the native wrapped token
  if (tokenAddress.toLowerCase() === chainConfig.nativeToken.wrappedAddress.toLowerCase()) {
    return chainConfig.nativeToken.decimals
  }

  // Check if it's a stablecoin
  const stablecoin = chainConfig.stablecoins.find(
    s => s.address.toLowerCase() === tokenAddress.toLowerCase()
  )
  if (stablecoin) {
    return stablecoin.decimals
  }

  // Check all configured tokens
  for (const tokenConfig of Object.values(TOKEN_PRICE_CONFIGS)) {
    const chainTokenConfig = tokenConfig.chains.find(c => c.chainId === chainId)
    if (chainTokenConfig && chainTokenConfig.address.toLowerCase() === tokenAddress.toLowerCase()) {
      return tokenConfig.decimals
    }
  }

  return 18
}

// Get token price helper
async function getTokenPrice(chainId: number, symbol: string): Promise<number | null> {
  // Check if it's a configured token
  if (TOKEN_PRICE_CONFIGS[symbol]) {
    return await getTokenPriceUsd(symbol, chainId)
  }
  // Check if it's a wrapped native token
  if (symbol.startsWith('W') && symbol.length <= 5) {
    return await getNativeTokenPriceUsd(chainId)
  }
  // Check if it's a stablecoin (USD-pegged)
  if (['USDT', 'USDC', 'BUSD', 'USDbC', 'DAI'].includes(symbol)) {
    return 1.0
  }
  return null
}

// Fetch a single pool's liquidity data
export async function fetchPoolLiquidity(
  poolId: PoolId,
  pool: LPPoolConfig
): Promise<PoolLiquidityData> {
  const chainConfig = CHAIN_PRICE_CONFIGS[poolId.chainId]
  if (!chainConfig) {
    throw new Error(`Unknown chain: ${poolId.chainId}`)
  }

  const provider = await getChainProvider(poolId.chainId)
  const multicall3Address = getMulticall3Address(poolId.chainId)
  if (!multicall3Address) {
    throw new Error(`No Multicall3 address for chain: ${poolId.chainId}`)
  }

  const multicall = createMulticallService(provider, multicall3Address, poolId.chainId)
  const poolIface = new Interface(POOL_ABI)
  const erc20Iface = new Interface(ERC20_ABI)

  // Phase 1: Get token addresses from pool
  const tokenAddressCalls = [
    multicall.encodeCall(pool.address, poolIface, 'token0', []),
    multicall.encodeCall(pool.address, poolIface, 'token1', []),
  ]

  const [token0Address, token1Address] = await multicall.callAndDecode<[string, string]>(tokenAddressCalls)

  // Phase 2: Get token balances in the pool
  const balanceCalls = [
    multicall.encodeCall(token0Address, erc20Iface, 'balanceOf', [pool.address]),
    multicall.encodeCall(token1Address, erc20Iface, 'balanceOf', [pool.address]),
  ]

  const [token0Balance, token1Balance] = await multicall.callAndDecode<[bigint, bigint]>(balanceCalls)

  // Get token info using actual addresses from pool
  const token0Symbol = getTokenSymbol(poolId.chainId, token0Address)
  const token1Symbol = getTokenSymbol(poolId.chainId, token1Address)
  const token0Decimals = getTokenDecimals(poolId.chainId, token0Address)
  const token1Decimals = getTokenDecimals(poolId.chainId, token1Address)

  // Format balances
  const token0BalanceFormatted = formatUnits(token0Balance, token0Decimals)
  const token1BalanceFormatted = formatUnits(token1Balance, token1Decimals)

  // Get prices
  const token0PriceUsd = await getTokenPrice(poolId.chainId, token0Symbol)
  const token1PriceUsd = await getTokenPrice(poolId.chainId, token1Symbol)

  // Calculate USD values
  const token0ValueUsd = token0PriceUsd !== null
    ? parseFloat(token0BalanceFormatted) * token0PriceUsd
    : null
  const token1ValueUsd = token1PriceUsd !== null
    ? parseFloat(token1BalanceFormatted) * token1PriceUsd
    : null
  const totalValueUsd = (token0ValueUsd !== null && token1ValueUsd !== null)
    ? token0ValueUsd + token1ValueUsd
    : null

  return {
    chainId: poolId.chainId,
    chainName: chainConfig.name,
    poolAddress: pool.address,
    dex: pool.dex,

    token0Address,
    token0Symbol,
    token0Balance,
    token0BalanceFormatted,
    token0Decimals,

    token1Address,
    token1Symbol,
    token1Balance,
    token1BalanceFormatted,
    token1Decimals,

    token0PriceUsd,
    token1PriceUsd,

    token0ValueUsd,
    token1ValueUsd,
    totalValueUsd,

    links: {
      explorer: getPoolExplorerUrl(poolId.chainId, pool.address),
      dexscreener: getDexscreenerUrl(poolId.chainId, pool.address),
    },
  }
}

// Get pool config from pool ID
export function getPoolConfig(poolId: PoolId): LPPoolConfig | null {
  const tokenConfig = TOKEN_PRICE_CONFIGS[poolId.tokenSymbol]
  if (!tokenConfig) return null

  const chainConfig = tokenConfig.chains.find(c => c.chainId === poolId.chainId)
  if (!chainConfig) return null

  return chainConfig.pools.find(p => p.address.toLowerCase() === poolId.poolAddress.toLowerCase()) || null
}

// Generic fetch wrapper that handles errors
export async function fetchPoolCacheEntry(
  poolId: PoolId,
  currentEntry: PoolCacheEntry,
  onUpdate: (entry: PoolCacheEntry) => void
): Promise<void> {
  const pool = getPoolConfig(poolId)
  if (!pool) {
    onUpdate({
      status: 'failed',
      data: null,
      error: 'Pool config not found',
      lastAttempt: Date.now(),
      retryCount: MAX_RETRIES, // Don't retry config errors
    })
    return
  }

  // Update to loading
  onUpdate({
    ...currentEntry,
    status: 'loading',
    lastAttempt: Date.now(),
  })

  try {
    const data = await fetchPoolLiquidity(poolId, pool)
    onUpdate({
      status: 'loaded',
      data,
      error: null,
      lastAttempt: Date.now(),
      retryCount: 0,
    })
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err)
    const isTransient = isTransientError(err)

    // Log appropriately
    if (isTransient) {
      console.warn(`[PoolCache] ${poolId.tokenSymbol} pool on chain ${poolId.chainId} temporarily unavailable`)
    } else {
      console.error(`[PoolCache] Failed to fetch pool ${poolId.poolAddress}:`, err)
    }

    onUpdate({
      status: 'failed',
      data: currentEntry.data, // Keep old data if available
      error: errorMsg,
      lastAttempt: Date.now(),
      retryCount: currentEntry.retryCount + 1,
    })
  }
}
